/**
 * API calls against the intake broker, plus the direct-to-GCS upload.
 *
 * Uses `fetch` rather than a shared Axios client on purpose. The signed upload
 * PUT must carry exactly the headers the broker signed — an interceptor that
 * added an Authorization or tracing header would invalidate the V4 signature —
 * so no request here may pass through shared default headers.
 */

import type {
  CreateUploadRequest,
  CreateUploadResponse,
  FormConfigResponse
} from '@/services/definitions'
import { describeApiError } from '@/utils/apiErrors'

const FORM_CONFIG_URL = '/api/v1/form/config'
const UPLOADS_URL = '/api/v1/uploads'

/** Fetch the bootstrap config the form needs before it can authenticate. */
export async function GetFormConfig(): Promise<FormConfigResponse> {
  const response = await fetch(FORM_CONFIG_URL)
  if (!response.ok) {
    throw new Error(`Could not load form configuration (HTTP ${response.status}).`)
  }
  return (await response.json()) as FormConfigResponse
}

/** Ask the broker for a scoped, short-lived upload target. */
export async function CreateUploadTarget(
  body: CreateUploadRequest,
  idToken: string
): Promise<CreateUploadResponse> {
  const response = await fetch(UPLOADS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    throw new Error(await describeApiError(response))
  }
  return (await response.json()) as CreateUploadResponse
}

/** Upload the app file straight to GCS using the broker's signed target. */
export async function UploadAppFile(
  target: CreateUploadResponse,
  file: File
): Promise<void> {
  const response = await fetch(target.upload_url, {
    method: target.upload_method,
    // Verbatim — these headers are covered by the signature.
    headers: target.required_headers,
    body: file
  })

  if (!response.ok) {
    // The body is a GCS XML error; keep it out of the UI but log it for support.
    console.error('Upload failed', response.status, await response.text())
    throw new Error(
      `The upload was rejected (HTTP ${response.status}). Your signed upload ` +
        'link may have expired — try submitting again.'
    )
  }
}
